/**
 * Utility functions for task status computation and display
 *
 * This is the SINGLE SOURCE OF TRUTH for all task status logic.
 * All components should import from this file to ensure consistency.
 */

import React from 'react'
import { CheckCircleIcon, AlertCircleIcon } from 'lucide-react'

/**
 * Task interface for type safety
 */
export interface Task {
  id?: string
  title: string
  studentId: string
  studentName?: string
  dueDate?: string | null
  deadline?: string // Legacy field
  dueAt?: string // Alternative field
  startDate?: string | null
  notes: string
  status: 'pending' | 'in-progress' | 'completed'
  reference?: string
  lessonId?: string
  createdAt?: string | null
  updatedAt?: string | null
}

/**
 * Display status type (includes computed 'overdue' status)
 */
export type TaskDisplayStatus =
  | 'completed'
  | 'overdue'
  | 'in-progress'
  | 'pending'

/**
 * Computes the display status of a task based on its dates and stored status
 *
 * RULES (deterministic and clear):
 * 1. If status is 'completed', always return 'completed'
 * 2. If not completed and today > dueDate, return 'overdue'
 * 3. If not completed and startDate ≤ today ≤ dueDate, return 'in-progress'
 * 4. If not completed and today < startDate, return 'pending'
 * 5. Default fallback: return 'pending'
 *
 * @param task - Task object with status and date fields
 * @returns Display status string
 */
export function getTaskDisplayStatus(task: {
  status?: string
  dueDate?: string | null
  deadline?: string
  dueAt?: string
  startDate?: string | null
  createdAt?: string | Date | null
}): TaskDisplayStatus {
  // Rule 1: Always prioritize completed status
  if (task.status === 'completed') return 'completed'

  const now = new Date()
  now.setHours(0, 0, 0, 0) // Normalize to start of day for consistent comparison

  // Get the due date from whichever field is available (consistent priority order)
  const dueDateStr = task.dueDate || task.deadline || task.dueAt

  if (!dueDateStr) return 'pending' // No due date, default to pending

  const dueDate = new Date(dueDateStr)
  dueDate.setHours(0, 0, 0, 0) // Normalize to start of day

  // Rule 2: Check if task is overdue
  if (now > dueDate) return 'overdue'

  // Get the start date (prefer explicit startDate over createdAt)
  let startDate: Date | null = null
  if (task.startDate) {
    startDate = new Date(task.startDate)
    startDate.setHours(0, 0, 0, 0)
  } else if (task.createdAt) {
    startDate =
      task.createdAt instanceof Date
        ? new Date(task.createdAt)
        : new Date(task.createdAt)
    startDate.setHours(0, 0, 0, 0)
  }

  // Rule 4: If no start date available, treat as pending
  if (!startDate) return 'pending'

  // Rule 4: If start date is in the future, task is pending
  if (now < startDate) return 'pending'

  // Rule 3: If we're between start date and due date, task is in progress
  if (now >= startDate && now <= dueDate) return 'in-progress'

  // Rule 5: Default fallback
  return 'pending'
}

/**
 * Renders a consistent status badge for tasks across all views
 *
 * This ensures Teacher and Student dashboards show identical badges.
 *
 * @param task - Task object
 * @param darkMode - Whether dark mode is enabled
 * @returns React element with styled badge
 */
export function getTaskStatusBadge(
  task: Task,
  darkMode: boolean,
): React.ReactElement {
  const displayStatus = getTaskDisplayStatus(task)

  if (displayStatus === 'completed') {
    return React.createElement(
      'span',
      {
        className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          darkMode
            ? 'bg-green-900/30 text-green-300'
            : 'bg-green-100 text-green-800'
        }`,
      },
      React.createElement(CheckCircleIcon, { className: 'h-3 w-3 mr-1' }),
      'Completed',
    )
  }

  if (displayStatus === 'overdue') {
    return React.createElement(
      'span',
      {
        className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
        }`,
      },
      React.createElement(AlertCircleIcon, { className: 'h-3 w-3 mr-1' }),
      'Overdue',
    )
  }

  if (displayStatus === 'in-progress') {
    return React.createElement(
      'span',
      {
        className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          darkMode
            ? 'bg-blue-900/30 text-blue-300'
            : 'bg-blue-100 text-blue-800'
        }`,
      },
      'In Progress',
    )
  }

  // Default: pending
  return React.createElement(
    'span',
    {
      className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        darkMode ? 'bg-gray-900/30 text-gray-300' : 'bg-gray-100 text-gray-800'
      }`,
    },
    'Pending',
  )
}

/**
 * Counts tasks by their display status
 *
 * Useful for metrics and statistics displays.
 *
 * @param tasks - Array of tasks to count
 * @returns Object with counts for each status
 */
export function countTasksByStatus(tasks: Task[]): {
  completed: number
  overdue: number
  inProgress: number
  pending: number
} {
  const counts = {
    completed: 0,
    overdue: 0,
    inProgress: 0,
    pending: 0,
  }

  tasks.forEach((task) => {
    const status = getTaskDisplayStatus(task)
    switch (status) {
      case 'completed':
        counts.completed++
        break
      case 'overdue':
        counts.overdue++
        break
      case 'in-progress':
        counts.inProgress++
        break
      case 'pending':
        counts.pending++
        break
    }
  })

  return counts
}
